import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import React from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { UIActions } from '../ui/uiSlice';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useShortcut from '../utils/useShortcut';
import * as styles from './ModalContainer.module.scss';
import ZoomInAnimation from './ZoomInAnimation';

const TRANSITION_CONFIG = {
  type: 'spring',
  stiffness: 1000,
  mass: 1,
  damping: 50,
} as const;

export type UnderlayType = 'default' | 'carouselSlider';

interface BaseModalContainerProps {
  open: boolean;
  alignToTop?: boolean;
  children: React.ReactNode;
  marginLeft?: number;
  underlayType?: UnderlayType;
}

export const BaseModalContainer = ({
  open,
  alignToTop,
  children,
  marginLeft,
  underlayType,
}: BaseModalContainerProps) => {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{
            opacity: 0,
            pointerEvents: 'none',
          }}
          animate={{
            opacity: 1,
            pointerEvents: 'auto',
          }}
          exit={{
            opacity: 0,
            // we don't want to be able to click through the modal while it's animating in or out
            // as we could accidentially double click on any confirming buttons, that triggered a close
            pointerEvents: 'none',
          }}
          transition={TRANSITION_CONFIG}
          style={{ left: marginLeft }}
          className={styles.container}
        >
          <ZoomInAnimation
            className={classNames(styles.inner, {
              [styles.shouldAlignToTop]: alignToTop,
            })}
          >
            {children}
          </ZoomInAnimation>
          <motion.div
            style={{ left: marginLeft }}
            className={classNames(styles.underlay, {
              [styles.carouselSlider]: underlayType === 'carouselSlider',
            })}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ModalContainer = () => {
  const {
    isOpen,
    View,
    props: {
      onClose,
      disableOutsideClose: shouldDisableOutsideClose,
      alignToTop: shouldAlignToTop,
      underlayType,
      ...props
    } = {},
  } = useAppSelector((state) => state.ui.modal);
  const dispatch = useAppDispatch();

  const close = () => {
    if (onClose) {
      onClose();
    }

    dispatch(UIActions.closeModal());
  };

  useShortcut(
    'esc',
    (e) => {
      if (!shouldDisableOutsideClose) {
        e.preventDefault();
        close();
      }
    },
    { global: true, skip: !isOpen },
  );

  return (
    <BaseModalContainer
      open={isOpen}
      alignToTop={shouldAlignToTop}
      underlayType={underlayType}
    >
      {View && (
        <ClickOutsideHandler
          className={styles.content}
          onClickOutside={shouldDisableOutsideClose ? undefined : close}
        >
          <View close={close} {...props} />
        </ClickOutsideHandler>
      )}
    </BaseModalContainer>
  );
};

export default ModalContainer;
